import locale from "localization";
import grid from "styles/grid.module.scss";
import ProductCode from "enums/product-code";
import { Text, Checkbox } from "components/commons";
import styles from "./shift-details-stock-inventory.module.scss";
import DipstickDetails from "./shift-details-stock-inventory-dipstick-details.module";
import DipstickInput from "../shift-details-stock-inventory-dipstick-input";
import React, { useCallback, useEffect } from "react";
import classNames from "classnames";
import { FormMode } from "enums";
import Validation from "utils/validation.utils";
import { validateField } from "hooks/useForm";
import { formatNumber, formatVolume } from "utils";

const PRODUCT_CONFIG = {
  [ProductCode.DIESEL]: { ugt: "1", label: "Diesel" },
  [ProductCode.GAS91]: { ugt: "2", label: "Gas 91" },
  [ProductCode.GAS95]: { ugt: "3", label: "Gas 95" },
  [ProductCode.GAS97]: { ugt: "4", label: "Gas 97" },
};

const formatFieldName = (type, str, unit) => {
  const capitalizedStr = str.charAt(0).toUpperCase() + str.slice(1);
  return `${type}${capitalizedStr}${unit}`;
};

export const validateReadingDipstick = (
  name,
  value,
  fields,
  fromCallback,
  shift,
  beginningDisabled,
) => {
  let data = { [name]: { value } };

  const type = name === "isReadingDipstickBeginning" ? "beginning" : "ending";
  const isDidNotPerform = value;

  Object.keys(fields).forEach((key) => {
    if (key.includes(type)) {
      const fieldValue =
        fromCallback && isDidNotPerform && shift && shift[key] != null
          ? formatVolume(shift[key], "")
          : "";

      const isCm = key.includes("Cm");
      const maxLiter = fields[key]?.maxLiter || (isCm ? 600 : 50000);
      const validations =
        isDidNotPerform || !fields[key].required
          ? []
          : [
              Validation.required({ noMessage: true }),
              Validation.min(isCm ? 0 : 0.001, {
                isFormatted: true,
                customMessage: <locale.Populate items={["0.000"]} text={locale.valueShouldNotBe} />,
              }),
              Validation.max(maxLiter, {
                isFormatted: true,
                customMessage: (
                  <locale.Populate
                    items={[formatNumber(maxLiter, 3)]}
                    text={locale.maxLiterShouldBe}
                  />
                ),
              }),
            ];

      data[key] = validateField(
        {
          ...fields[key],
          disabled: isDidNotPerform,
          value: fieldValue,
          validations,
        },
        fields,
      );
    }
  });

  return data;
};

const ShiftDetailsDipstick = ({
  shift,
  progress,
  pageMode,
  form,
  endDidNotPerformDipStick,
  setEndDidNotPerformDipStick,
}) => {
  const { fields, modifyField, modifyForm } = form;
  const stationProducts = shift.station.stationUgt.map((item) => item.productCode);
  const beginningDisabled = shift.recentShift !== null;
  const hasTotalizerData = (shift?.shiftPumpTotals?.length || 0) > 0;

  const hasInitializedRef = React.useRef(false);

  useEffect(() => {
    if (hasInitializedRef.current) return;

    if (beginningDisabled) {
      const data = {};
      stationProducts.forEach((product) => {
        const beginningCm = formatFieldName("beginning", product, "Cm");
        const beginningL = formatFieldName("beginning", product, "");
        const endingCm = formatFieldName("ending", product, "Cm");
        const endingL = formatFieldName("ending", product, "");

        const wasProductNewlyAdded =
          shift.recentShift[endingCm] === 0 && shift.recentShift[endingL] === 0;

        if (!wasProductNewlyAdded) {
          data[beginningCm] = {
            ...fields[beginningCm],
            value: formatVolume(shift.recentShift[endingCm], ""),
            disabled: !wasProductNewlyAdded,
          };
          data[beginningL] = {
            ...fields[beginningL],
            value: formatVolume(shift.recentShift[endingL], ""),
            disabled: !wasProductNewlyAdded,
          };
        }
      });
      modifyForm({ ...fields, ...data });
    }

    hasInitializedRef.current = true;
  }, [beginningDisabled, shift.recentShift, stationProducts, modifyForm, fields]);

  const isDidNotPerform = useCallback(
    (name, value) => {
      const validatedFields = validateReadingDipstick(
        name,
        value,
        fields,
        true,
        shift.recentShift,
        beginningDisabled,
      );
      const data = { [name]: { value }, ...validatedFields };

      setEndDidNotPerformDipStick(value);

      modifyForm({ ...fields, ...data });
      return data;
    },
    [beginningDisabled, fields, modifyForm, setEndDidNotPerformDipStick, shift.recentShift],
  );

  const renderDipstickDetails = (product, isEnding = false, position) => (
    <DipstickDetails
      key={product}
      label={`${locale.ugt}${position}`}
      progress={progress}
      shift={shift}
      product={product}
      isEndingDipstick={isEnding}
    />
  );

  const renderDipstickInputs = (product, type = "beginning", position) => {
    const cmField = formatFieldName(type, product, "Cm");
    const literField = formatFieldName(type, product, "");

    return (
      <div className={styles.dipstick} key={`${type}-${product}`}>
        <Text className={classNames(styles.classLabel, styles[product])}>
          {`UGT ${position} (${PRODUCT_CONFIG[product].label})`}
        </Text>
        <div
          className={classNames({
            [`${styles.spacerBottom}`]: type === "beginning",
          })}
        >
          <div className={styles.row}>
            <div className={styles.columnFluid}>
              <DipstickInput
                fields={fields?.[cmField]}
                unit="cm"
                placeholder="e.g 100.000"
                modifyField={modifyField}
                disabled={fields?.[cmField].disabled}
              />
            </div>
            <div className={styles.columnFluid}>
              <DipstickInput
                fields={fields?.[literField]}
                unit="L"
                placeholder="e.g 100.000"
                modifyField={modifyField}
                disabled={fields?.[literField].disabled}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.subContainer}>
      <div className={grid.row}>
        <div className={styles.column}>
          {pageMode === FormMode.View && (
            <>
              <Text className={styles.header}>{locale.beginningInventory}</Text>
              {!shift?.shift.isReadingDipstickBeginning && progress === 100.0 ? (
                <>
                  <div className={styles.spacer10} />
                  <Text className={styles.content}>{locale.didNotPerformDipstickReading}</Text>
                </>
              ) : (
                stationProducts.map(
                  (product, i) =>
                    PRODUCT_CONFIG[product] && renderDipstickDetails(product, false, i + 1),
                )
              )}
              <div className={styles.spacer50} />
            </>
          )}
        </div>
        <div className={styles.column}>
          {pageMode === FormMode.View && (
            <>
              <Text className={styles.header}>{locale.endingInventory}</Text>
              {!shift?.shift.isReadingDipstickEnding && progress === 100.0 ? (
                <>
                  <div className={styles.spacer10} />
                  <Text className={styles.content}>{locale.didNotPerformDipstickReading}</Text>
                </>
              ) : (
                stationProducts.map(
                  (product, i) =>
                    PRODUCT_CONFIG[product] && renderDipstickDetails(product, true, i + 1),
                )
              )}
              <div className={styles.spacer50} />
            </>
          )}
        </div>
      </div>

      {pageMode === FormMode.Edit && (
        <div className={styles.editSalesContainer}>
          <div className={styles.beginningInventory}>
            <Text className={styles.header}>{locale.beginningInventory}</Text>
            <div className={styles.spacerTop}>
              {stationProducts.map((product, i) =>
                renderDipstickInputs(product, "beginning", i + 1),
              )}
            </div>
          </div>

          <div className={styles.endingInventory}>
            <Text className={styles.header}>{locale.endingInventory}</Text>
            <div className={styles.checkBox}>
              <Checkbox
                {...fields?.isReadingDipstickEnding}
                disabled={!hasTotalizerData}
                onChange={(name, { value }) => isDidNotPerform(name, value)}
              />
              {"Did not perform dipstick reading"}
            </div>
            {stationProducts.map((product, i) => renderDipstickInputs(product, "ending", i + 1))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ShiftDetailsDipstick;
